/**
 * AWS client credentials for browser-direct AWS Transcribe streaming.
 *
 * Exchanges the Cognito User Pool ID token for short-lived AWS credentials via
 * the SystemDox media Identity Pool (created in backend/template.yaml, ADR-022
 * Rev 2 item 4). Only Transcribe streaming needs direct SDK access; all other
 * data access goes through the REST services.
 *
 * Ported from product-noteble (apps/app/src/lib/awsClients.ts), adapted to
 * SystemDox: the ID token comes from @aws-amplify/auth's fetchAuthSession
 * (Amplify is configured by @puglieseweb/shared-react-auth), and the whole
 * feature is gated behind isTranscribeConfigured() so the capture app falls
 * back to browser Web Speech dictation until the env is wired in CI.
 */

// Import from the specific subpackage (not the @aws-sdk/credential-providers
// barrel): the barrel pulls in the node-only login/process/sso providers,
// which break the browser build. This provider is browser-safe.
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';

export const AWS_REGION = import.meta.env.REACT_APP_AWS_REGION || 'eu-west-2';

/** The Cognito User Pool provider key used in the Identity Pool logins map. */
export function cognitoProviderKey(region: string, userPoolId: string): string {
  return `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
}

/**
 * Whether AWS Transcribe streaming is configured for this build. Read live
 * (not module-load consts) so it reflects the current env. When false, the
 * capture app uses browser Web Speech dictation instead.
 */
export function isTranscribeConfigured(): boolean {
  return Boolean(
    import.meta.env.REACT_APP_IDENTITY_POOL_ID && import.meta.env.REACT_APP_USER_POOL_ID,
  );
}

/**
 * Credentials provider that assumes the media authenticated role via the
 * Identity Pool. Throws if unconfigured or the user is not signed in.
 */
export async function getCredentialsProvider() {
  const identityPoolId = import.meta.env.REACT_APP_IDENTITY_POOL_ID as string | undefined;
  const userPoolId = import.meta.env.REACT_APP_USER_POOL_ID as string | undefined;
  if (!identityPoolId || !userPoolId) {
    throw new Error('Identity Pool or User Pool not configured');
  }

  // Dynamic import keeps @aws-amplify/auth out of the initial bundle; Amplify
  // is already configured by shared-react-auth's configureAuth().
  const { fetchAuthSession } = await import('@aws-amplify/auth');
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new Error('No authenticated session — user must be signed in');
  }

  return fromCognitoIdentityPool({
    clientConfig: { region: AWS_REGION },
    identityPoolId,
    logins: {
      [cognitoProviderKey(AWS_REGION, userPoolId)]: idToken,
    },
  });
}
